import React, { useCallback } from 'react';
import { useState, InputHTMLAttributes, ChangeEvent } from 'react';
import styled from '@emotion/styled';
import { Flex, FlexColumn, FlexCenter } from '../Flex';
import { ReactComponent as Search } from '~/shared/assets/magnify.svg';
import { ReactComponent as Close } from '~/shared/assets/X.svg';

const BaseInput = styled('input')`
  height: 22px;
  padding: 0 3px 3px;
  font-size: 14px;
  background-color: var(--input-background) !important;

  color: var(--input-text-color);
  border-color: var(--input-border-color);
  border-width: 0 0 1px 0;
  border-radius: 0;
  font-weight: 700;
  text-align: inherit;
  outline: 0;
  resize: none;

  ::placeholder {
    font-style: italic !important;
    font-weight: 400;
    color: var(--input-place-holder);
    text-transform: capitalize;
  }

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
  :disabled {
    cursor: not-allowed;
    color: gray;
    background: transparent;
  }
`;

const Container = styled(FlexColumn)`
  flex-grow: 1;
  input {
    :hover {
      border-color: var(--input-border-color-hover);
    }
  }
`;

const Wrapper = styled(FlexCenter)<{ focus: number }>`
  position: absolute;
  z-index: 3;
  top: 0px;
  left: 10px;
  width: ${({ focus }) => (focus ? '691' : '711')}px;
  justify-content: flex-start;
  height: 40px;
  max-height: 40px;
  text-align: left !important;
  background-color: ${({ focus }) => (focus ? 'transparent' : 'var(--search-background-color)')};
  border-radius: 30px;
  border: ${({ focus }) => (focus ? 'none' : '1px solid var(--color-border)')};
  padding: 0;
  margin: 0;

  input {
    color: var(--search-text-color) !important;
    background: transparent !important;
    border: none;
    width: 630px;
    font-size: 15px;
    line-height: 18px;
    margin-top: 1px;
    padding: 0 10px 0 0;

    ::placeholder {
      font-size: 15px;
      line-height: 18px;
      color: var(--text-color);
      font-style: normal !important;
    }
  }
  .input:focus-within {
    color: var(--search-input-focus) !important;
  }
`;

const StyledSearchIcon = styled(Search)<{ over: number; focus: number; disabled: boolean }>`
  margin: 0 8px 0 20px;
  color: var(--search-icon-color);
  fill: var(--search-icon-color);
  ${({ focus }) => focus && `color: var(--search-icon-color-focus);fill: var(--search-icon-color-focus);`};
  :active {
    color: var(--search-icon-color-active);
    fill: var(--search-icon-color-active);
  }
  cursor: default;
`;

const StyledCloseIcon = styled(Close)`
  margin: 0 2px 0 0;
  cursor: pointer;
  color: var(--close-icon-color);
  fill: var(--close-icon-color);
  :hover {
    color: var(--close-icon-color-hover);
    fill: var(--close-icon-color-hover);
  }
  :active {
    color: var(--close-icon-color-active);
    fill: var(--close-icon-color-active);
  }
`;

const IconsContainer = styled(Flex)`
  justify-content: flex-end;
  min-width: 40px;
`;

export interface SearchBoxProps extends InputHTMLAttributes<HTMLInputElement> {
  value: string;
  placeholder?: string;
  onSearch?: () => void;
  onValueChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onValueReset: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  secondary?: boolean;
  usesEnter?: boolean;
  setIsOpen?: (val: boolean) => void;
  isSearchVisible?: boolean;
}

const SearchBox = ({
  value,
  placeholder = 'Search',
  onKeyDown,
  onSearch,
  onValueChange,
  onValueReset,
  onFocus,
  disabled,
  usesEnter,
  isSearchVisible,
  setIsOpen,
  ...inputProps
}: SearchBoxProps) => {
  const [isOver, setIsOver] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const handleFocus = useCallback(() => {
    if (onFocus) onFocus();
    setIsFocus(true);
    if (setIsOpen) setIsOpen(true);
  }, [onFocus]);
  const handleBlur = useCallback(() => {
    setIsFocus(false);
  }, [onFocus]);

  const handleChange = useCallback(
    (e: any) => {
      onValueChange(e);
    },
    [onFocus],
  );

  return (
    <Wrapper focus={isSearchVisible ? 1 : 0}>
      {usesEnter ? (
        <IconsContainer>
          {value !== '' && <StyledCloseIcon role="button" onClick={() => onValueReset()} transform="scale(0.8, 0.8)" />}
          <StyledSearchIcon
            onClick={() => onSearch && onSearch()}
            over={!!(isOver.toString() === 'true' ? 'true' : undefined) ? 1 : 0}
            focus={!!(isFocus.toString() === 'true' ? 'true' : undefined) ? 1 : 0}
            disabled={value === ''}
          />
        </IconsContainer>
      ) : (
        <StyledSearchIcon
          over={!!(isOver.toString() === 'true' ? 'true' : undefined) ? 1 : 0}
          focus={!!(isFocus.toString() === 'true' ? 'true' : undefined) ? 1 : 0}
          disabled={disabled || false}
        />
      )}
      <FlexColumn
        onMouseEnter={() => setIsOver(true)}
        onMouseLeave={() => setIsOver(false)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
      >
        <Flex>
          <Container>
            <Flex>
              <BaseInput
                onKeyDown={(evt: any) => evt.key === '.' && evt.preventDefault()}
                value={value && String(value)}
                onChange={handleChange}
                placeholder={placeholder}
                autoComplete="off"
                {...inputProps}
              />
            </Flex>
          </Container>
        </Flex>
      </FlexColumn>
      {isSearchVisible && <StyledCloseIcon role="button" onClick={() => onValueReset()} transform="scale(0.8, 0.8)" />}
    </Wrapper>
  );
};

export default SearchBox;
